import React, { useState, useEffect } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Switch, TextField, IconButton, Button, Typography,
  Select, MenuItem, FormControl, Checkbox, ListItemText,
  CircularProgress, Box,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import SaveIcon from "@material-ui/icons/Save";
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Edit";
import axios from "axios";
import { appUrl } from "../../../../../utils/config";

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    marginTop: theme.spacing(2),
    border: "1px solid #e0e0e0",
    borderRadius: 6,
    overflow: "hidden",
  },
  headerCell: {
    fontWeight: 700,
    backgroundColor: "#f0f0f0",
    color: "#333",
    borderRight: "1px solid #e0e0e0",
    fontSize: 13,
    padding: "10px 14px",
  },
  bodyCell: {
    borderRight: "1px solid #f0f0f0",
    padding: "8px 14px",
    fontSize: 13,
  },
  tableRow: {
    "&:hover": { backgroundColor: "#fafafa" },
    "&:last-child td": { borderBottom: 0 },
  },
  saveBtn: {
    backgroundColor: "#E87722",
    color: "#fff",
    fontWeight: 600,
    borderRadius: 24,
    padding: "8px 24px",
    textTransform: "none",
    "&:hover": { backgroundColor: "#cf6510" },
  },
  resetBtn: {
    border: "2px solid #333",
    color: "#333",
    fontWeight: 600,
    borderRadius: 24,
    padding: "6px 20px",
    textTransform: "none",
    backgroundColor: "#fff",
    "&:hover": { backgroundColor: "#f5f5f5" },
  },
  addRowBtn: {
    border: "1.5px solid #555",
    color: "#333",
    fontWeight: 600,
    borderRadius: 24,
    padding: "5px 18px",
    textTransform: "none",
    backgroundColor: "#fff",
    "&:hover": { backgroundColor: "#f5f5f5" },
  },
  deleteIcon: { color: "#d32f2f" },
  saveRowIcon: { color: "#1f201f" },
  cancelRowIcon: { color: "#757575" },
  actionButton: { marginRight: theme.spacing(1) },
}));

const ShipmentConfigTab = (props) => {
  const classes = useStyles();
  const { featuresAccess } = props;
  const canEdit = featuresAccess?.editSponsorNetworkDetails ?? false;

  const [shipmentConfigs, setShipmentConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isUpdated, setIsUpdated] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  // Products fetched from FetchProductDetailsForShipmentConfig for new row
  const [productOptions, setProductOptions] = useState([]);
  const [productLoading, setProductLoading] = useState(false);
  const [dirtyIndices, setDirtyIndices] = useState(new Set());
  const [productSearch, setProductSearch] = useState("");
  const [cmoSearch, setCmoSearch] = useState("");

  // --- Static fallback data for testing ---
  const STATIC_SHIPMENT_CONFIGS = [
    {
      linear_id: "a1b2c3d4-0001",
      collabrixProductID: 101,
      productCode: "DRUG-XYZ",
      productDescription: "Some Drug Description",
      vendorMosymphonyID: 55,
      vendorName: "CMO Pharma Ltd",
      maxPostingQtyAllowed: 5000.0,
      uom: "KG",
      isActive: true,
      markedForDelete: null,
    },
    {
      linear_id: "a1b2c3d4-0002",
      collabrixProductID: 102,
      productCode: "DRUG-ABC",
      productDescription: "Another Drug Description",
      vendorMosymphonyID: 56,
      vendorName: "BioManufacturing Co",
      maxPostingQtyAllowed: 3000.0,
      uom: "LTR",
      isActive: true,
      markedForDelete: null,
    },
  ];
  // -----------------------------------------

  useEffect(() => {
    fetchShipmentConfigs();
  }, []);

  const fetchShipmentConfigs = async () => {
    setLoading(true);
    try {
      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchShipmentConfig`;
      const response = await axios.get(url, {});
      const list = response.data?.status ? (response.data?.data || []) : STATIC_SHIPMENT_CONFIGS;
      const formatted = list.map((item) => ({
        ...item,
        isNew: false,
        isEditing: false,
      }));
      setShipmentConfigs(formatted);
      props.onShipmentConfigsFetched?.(formatted);
    } catch (err) {
      console.error("Error fetching shipment configs, using static data:", err);
      const formatted = STATIC_SHIPMENT_CONFIGS.map((item) => ({
        ...item,
        isNew: false,
        isEditing: false,
      }));
      setShipmentConfigs(formatted);
      props.onShipmentConfigsFetched?.(formatted);
    } finally {
      setLoading(false);
    }
  };

  // Fetch product list for the new-row dropdown
  const fetchProductOptions = async () => {
    setProductLoading(true);
    try {
      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/FetchProductDetailsForShipmentConfig`;
      const response = await axios.get(url);
      const list = response.data?.data?.productList;
      if (response.data?.status && Array.isArray(list)) {
        setProductOptions(list);
      } else {
        setProductOptions([]);
      }
    } catch (err) {
      console.error("Error fetching product options:", err);
      setProductOptions([]);
    } finally {
      setProductLoading(false);
    }
  };

  // Unique CMOs from productOptions (for new-row CMO dropdown)
  const getUniqueCmosFromOptions = () => {
    const seen = new Set();
    return productOptions.filter((p) => {
      if (seen.has(p.vendorMosymphonyID)) return false;
      seen.add(p.vendorMosymphonyID);
      return true;
    });
  };

  // Products available for a given CMO (excludes already configured products)
  const getProductsForCmo = (vendorMosymphonyID) => {
    const existingIds = new Set(
      shipmentConfigs
        .filter((c) => !c.isNew)
        .map((c) => c.collabrixProductID)
    );
    const seen = new Set();
    return productOptions.filter((p) => {
      if (p.vendorMosymphonyID !== vendorMosymphonyID) return false;
      if (existingIds.has(p.collabrixProductID)) return false;
      if (seen.has(p.collabrixProductID)) return false;
      seen.add(p.collabrixProductID);
      return true;
    });
  };

  // Unique products from productOptions (for new-row dropdown) — used in existing edit flow
  // Excludes collabrixProductIDs already configured in existing shipmentConfigs
  const getUniqueProductsFromOptions = () => {
    const existingIds = new Set(
      shipmentConfigs
        .filter((c) => !c.isNew)
        .map((c) => c.collabrixProductID)
    );
    const seen = new Set();
    return productOptions.filter((p) => {
      if (existingIds.has(p.collabrixProductID)) return false;
      if (seen.has(p.collabrixProductID)) return false;
      seen.add(p.collabrixProductID);
      return true;
    });
  };

  // CMO list for a product from productOptions (for new-row CMO dropdown)
  const getCmosForProductFromOptions = (collabrixProductID) =>
    productOptions
      .filter((p) => p.collabrixProductID === collabrixProductID)
      .map((p) => ({
        vendorMosymphonyID: p.vendorMosymphonyID,
        vendorName: p.vendorName,
        uom: p.uom,
        brandOwnerMosymphonyID: p.brandOwnerMosymphonyID || "",
      }));

  // Derive unique products from fetched shipment configs (non-new rows only)
  const getUniqueProducts = () => {
    const seen = new Set();
    return shipmentConfigs
      .filter((c) => !c.isNew)
      .filter((c) => {
        if (seen.has(c.collabrixProductID)) return false;
        seen.add(c.collabrixProductID);
        return true;
      });
  };

  // Derive CMO options for a given product from fetched shipment configs
  const getCmosForProduct = (collabrixProductID) =>
    shipmentConfigs
      .filter((c) => !c.isNew && c.collabrixProductID === collabrixProductID)
      .map((c) => ({ vendorMosymphonyID: c.vendorMosymphonyID, vendorName: c.vendorName, uom: c.uom }));

  const addNewRow = async () => {
    await fetchProductOptions();
    setShipmentConfigs((prev) => [
      ...prev,
      {
        id: null,
        collabrixProductID: "",
        productCode: "",
        productDescription: "",
        vendorMosymphonyID: "",  // single value — CMO selected first
        vendorName: "",
        maxPostingQtyAllowed: "",
        uom: "",
        isActive: true,
        isNew: true,
        isEditing: true,
      },
    ]);
  };

  // Handler for CMO selection in new rows (CMO-first flow)
  const handleCmoChangeForNewRow = (index, vendorMosymphonyID) => {
    const cmo = productOptions.find((p) => p.vendorMosymphonyID === vendorMosymphonyID);
    const updated = [...shipmentConfigs];
    updated[index] = {
      ...updated[index],
      vendorMosymphonyID,
      vendorName: cmo?.vendorName || "",
      // reset product when CMO changes
      collabrixProductID: "",
      productCode: "",
      productDescription: "",
      uom: "",
    };
    setShipmentConfigs(updated);
  };

  const handleProductChange = (index, collabrixProductID) => {
    const item = shipmentConfigs[index];
    const updated = [...shipmentConfigs];

    if (item.isNew) {
      // New row: product is selected after CMO, find match for that specific CMO
      const matchingRecord = productOptions.find(
        (p) =>
          p.collabrixProductID === collabrixProductID &&
          p.vendorMosymphonyID === item.vendorMosymphonyID
      );
      updated[index] = {
        ...updated[index],
        collabrixProductID,
        productCode: matchingRecord?.productCode || "",
        productDescription: matchingRecord?.productDescription || "",
        uom: matchingRecord?.uom || "",
      };
    } else {
      // Existing edit row: original product-first behavior preserved
      const source = shipmentConfigs.filter((c) => !c.isNew);
      const matchingRecord = source.find((p) => p.collabrixProductID === collabrixProductID);
      const cmos = getCmosForProduct(collabrixProductID);
      const allCmoIds = cmos.map((c) => c.vendorMosymphonyID);
      updated[index] = {
        ...updated[index],
        collabrixProductID,
        productCode: matchingRecord?.productCode || "",
        productDescription: matchingRecord?.productDescription || "",
        vendorMosymphonyID: allCmoIds,
        uom: cmos[0]?.uom || matchingRecord?.uom || "",
      };
    }

    setShipmentConfigs(updated);
  };

  const handleCmoChange = (index, selectedValues) => {
    const item = shipmentConfigs[index];
    const cmos = item.isNew
      ? getCmosForProductFromOptions(item.collabrixProductID)
      : getCmosForProduct(item.collabrixProductID);

    // Handle "All CMOs" toggle
    if (selectedValues.includes("all")) {
      const allIds = cmos.map((c) => c.vendorMosymphonyID);
      const updated = [...shipmentConfigs];
      const allSelected = updated[index].vendorMosymphonyID.length === cmos.length && cmos.length > 0;
      updated[index].vendorMosymphonyID = allSelected ? [] : allIds;
      if (!allSelected && cmos.length > 0) {
        updated[index].uom = cmos[0]?.uom || "";
      }
      setShipmentConfigs(updated);
      return;
    }

    const updated = [...shipmentConfigs];
    updated[index].vendorMosymphonyID = selectedValues;
    if (selectedValues.length > 0) {
      const firstCmo = cmos.find((c) => c.vendorMosymphonyID === selectedValues[0]);
      updated[index].uom = firstCmo?.uom || "";
    }
    setShipmentConfigs(updated);
  };

  const handleMaxQtyChange = (index, rawValue) => {
    const numericValue = rawValue.replace(/[^0-9.]/g, "");
    const updated = [...shipmentConfigs];
    updated[index].maxPostingQtyAllowed = numericValue;
    setShipmentConfigs(updated);
  };

  const formatWithCommas = (value) => {
    if (!value && value !== 0) return "";
    const num = parseFloat(value);
    if (isNaN(num)) return "";
    return num.toLocaleString("en-US");
  };

  const handleEditStart = (index) => {
    const updated = [...shipmentConfigs];
    updated[index]._originalMaxQty = updated[index].maxPostingQtyAllowed;
    if (!Array.isArray(updated[index].vendorMosymphonyID)) {
      updated[index].vendorMosymphonyID = updated[index].vendorMosymphonyID != null
        ? [updated[index].vendorMosymphonyID]
        : [];
    }
    updated[index].isEditing = true;
    setShipmentConfigs(updated);
  };

  const handleEditCancel = (index) => {
    const updated = [...shipmentConfigs];
    updated[index].maxPostingQtyAllowed = updated[index]._originalMaxQty;
    updated[index].isEditing = false;
    setShipmentConfigs(updated);
  };

  const handleToggle = (index) => {
    const updated = [...shipmentConfigs];
    updated[index].isActive = !updated[index].isActive;
    setShipmentConfigs(updated);
    setIsUpdated(true);
    setDirtyIndices((prev) => new Set(prev).add(index));
  };

  const saveSelection = async (index) => {
    const item = shipmentConfigs[index];
    const qty = parseFloat(item.maxPostingQtyAllowed);
    if (!item.collabrixProductID || isNaN(qty) || qty <= 0) {
      setError("Please fill all required fields. Max Qty must be greater than 0.");
      return;
    }
    setError(null);
    try {
      if (item.isNew) {
        // Build one payload entry per selected CMO
        const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/addShipmentConfig`;
        const cmoIds = Array.isArray(item.vendorMosymphonyID)
          ? item.vendorMosymphonyID
          : [item.vendorMosymphonyID];

        const payload = cmoIds.map((cmoId) => ({
          collabrixProductID: item.collabrixProductID,
          vendorMosymphonyID: cmoId,
          maxPostingQtyAllowed: qty,
          isActive: item.isActive,
          markedForDelete: false,
          uom: item.uom || "",
        }));

        await axios.post(url, payload);
      } else {
        const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateShipmentConfig`;
        const cmoIds = Array.isArray(item.vendorMosymphonyID)
          ? item.vendorMosymphonyID
          : [item.vendorMosymphonyID];
        const payload = cmoIds.map((cmoId) => ({
          collabrixProductID: item.collabrixProductID,
          vendorMosymphonyID: cmoId,
          maxPostingQtyAllowed: qty,
          isActive: item.isActive,
          markedForDelete: false,
          uom: item.uom,
        }));
        await axios.post(url, payload);
      }
    } catch (err) {
      console.error("Error saving config:", err);
      setError("Failed to save shipment configuration.");
    }
  };

  // Wrapper for the inline save button — saves the row then refreshes once
  const handleSaveRow = async (index) => {
    await saveSelection(index);
    fetchShipmentConfigs();
  };

  const handleSaveSelection = async () => {
    setSaveLoading(true);
    setError(null);
    try {
      const dirtyArray = Array.from(dirtyIndices);
      const newRowIndices = dirtyArray.filter((i) => shipmentConfigs[i]?.isNew);
      const existingRowIndices = dirtyArray.filter((i) => !shipmentConfigs[i]?.isNew);

      // New rows: handled individually via addShipmentConfig (existing behaviour)
      for (const index of newRowIndices) {
        await saveSelection(index);
      }

      // Existing rows: batch all toggle/edit changes into a single updateShipmentConfig call
      if (existingRowIndices.length > 0) {
        const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateShipmentConfig`;
        const batchPayload = existingRowIndices.flatMap((index) => {
          const item = shipmentConfigs[index];
          const qty = parseFloat(item.maxPostingQtyAllowed);
          const cmoIds = Array.isArray(item.vendorMosymphonyID)
            ? item.vendorMosymphonyID
            : [item.vendorMosymphonyID];
          return cmoIds.map((cmoId) => ({
            collabrixProductID: item.collabrixProductID,
            vendorMosymphonyID: cmoId,
            maxPostingQtyAllowed: isNaN(qty) ? 0 : qty,
            isActive: item.isActive,
            markedForDelete: false,
            uom: item.uom,
          }));
        });
        await axios.post(url, batchPayload);
      }

      // Single refresh after all saves are done
      fetchShipmentConfigs();
      setDirtyIndices(new Set());
      setIsUpdated(false);
    } catch (err) {
      console.error("Error saving config:", err);
      setError("Failed to save shipment configuration.");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async (index) => {
    const item = shipmentConfigs[index];
    if (item.isNew) {
      setShipmentConfigs((prev) => prev.filter((_, i) => i !== index));
      return;
    }
    try {
      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateShipmentConfig`;
      const cmoIds = Array.isArray(item.vendorMosymphonyID)
        ? item.vendorMosymphonyID
        : [item.vendorMosymphonyID];
      const deletePayload = cmoIds.map((cmoId) => ({
        collabrixProductID: item.collabrixProductID,
        vendorMosymphonyID: cmoId,
        maxPostingQtyAllowed: parseFloat(item.maxPostingQtyAllowed) || 0,
        isActive: item.isActive,
        markedForDelete: true,
        uom: item.uom,
      }));
      await axios.post(url, deletePayload);
      fetchShipmentConfigs();
    } catch (err) {
      console.error("Error deleting config:", err);
    }
  };

  const resetSelection = () => {
    fetchShipmentConfigs();
    setIsUpdated(false);
  };

  return (
    <div>
      {error && (
        <Typography color="error" style={{ marginBottom: 8 }}>{error}</Typography>
      )}

      <Box display="flex" alignItems="flex-start" style={{ gap: 16 }}>
        <Box flex={1}>
          <TableContainer component={Paper} className={classes.tableContainer} elevation={0}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.headerCell}>Selection</TableCell>
                  <TableCell className={classes.headerCell}>CMO</TableCell>
                  <TableCell className={classes.headerCell}>BMS Product Code / Description</TableCell>
                  <TableCell className={classes.headerCell}>Maximum Posting Qty Allowed</TableCell>
                  <TableCell className={classes.headerCell}>UoM</TableCell>
                  <TableCell className={classes.headerCell} style={{ borderRight: "none" }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center"><CircularProgress size={24} /></TableCell>
                  </TableRow>
                ) : shipmentConfigs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2">No shipment configurations found.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  shipmentConfigs.map((item, index) => (
                    <TableRow key={index} className={classes.tableRow}>

                      {/* Selection (toggle) */}
                      <TableCell className={classes.bodyCell}>
                        <Switch
                          checked={item.isActive}
                          onChange={() => handleToggle(index)}
                          disabled={!canEdit}
                          color="secondary"
                        />
                      </TableCell>

                      {/* CMO column — first */}
                      <TableCell className={classes.bodyCell}>
                        {item.isNew ? (
                          // New row: CMO single-select (user picks CMO first)
                          <FormControl fullWidth size="small">
                            {productLoading ? (
                              <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                                <CircularProgress size={14} />
                                <Typography variant="caption">Loading...</Typography>
                              </Box>
                            ) : (
                              <Select
                                value={item.vendorMosymphonyID}
                                onChange={(e) => handleCmoChangeForNewRow(index, e.target.value)}
                                displayEmpty
                                style={{ fontSize: 13 }}
                                onClose={() => setCmoSearch("")}
                                MenuProps={{ autoFocus: false }}
                              >
                                <MenuItem disabled style={{ padding: "4px 8px" }}>
                                  <TextField
                                    size="small"
                                    placeholder="Search CMO..."
                                    value={cmoSearch}
                                    onChange={(e) => setCmoSearch(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    onKeyDown={(e) => e.stopPropagation()}
                                    fullWidth
                                    autoFocus
                                    inputProps={{ style: { fontSize: 12 } }}
                                  />
                                </MenuItem>
                                <MenuItem value="" disabled>Select from dropdown</MenuItem>
                                {getUniqueCmosFromOptions()
                                  .filter((p) =>
                                    !cmoSearch ||
                                    p.vendorName?.toLowerCase().includes(cmoSearch.toLowerCase())
                                  )
                                  .map((p) => (
                                    <MenuItem key={p.vendorMosymphonyID} value={p.vendorMosymphonyID}>
                                      {p.vendorName}
                                    </MenuItem>
                                  ))}
                              </Select>
                            )}
                          </FormControl>
                        ) : (
                          item.vendorName || ""
                        )}
                      </TableCell>

                      {/* Product Code / Description column — second */}
                      <TableCell className={classes.bodyCell}>
                        {item.isNew ? (
                          // New row: product filtered by selected CMO
                          <FormControl fullWidth size="small">
                            {productLoading ? (
                              <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                                <CircularProgress size={14} />
                                <Typography variant="caption">Loading...</Typography>
                              </Box>
                            ) : (
                              <Select
                                value={item.collabrixProductID}
                                onChange={(e) => handleProductChange(index, e.target.value)}
                                displayEmpty
                                disabled={!item.vendorMosymphonyID}
                                style={{ fontSize: 13 }}
                                onClose={() => setProductSearch("")}
                                MenuProps={{ autoFocus: false }}
                              >
                                <MenuItem disabled style={{ padding: "4px 8px" }}>
                                  <TextField
                                    size="small"
                                    placeholder="Search product code or description..."
                                    value={productSearch}
                                    onChange={(e) => setProductSearch(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    onKeyDown={(e) => e.stopPropagation()}
                                    fullWidth
                                    autoFocus
                                    inputProps={{ style: { fontSize: 12 } }}
                                  />
                                </MenuItem>
                                <MenuItem value="" disabled>
                                  {item.vendorMosymphonyID ? "Select from dropdown" : "Select CMO first"}
                                </MenuItem>
                                {getProductsForCmo(item.vendorMosymphonyID)
                                  .filter((p) =>
                                    !productSearch ||
                                    p.productCode?.toLowerCase().includes(productSearch.toLowerCase()) ||
                                    p.productDescription?.toLowerCase().includes(productSearch.toLowerCase())
                                  )
                                  .map((p) => (
                                    <MenuItem key={p.collabrixProductID} value={p.collabrixProductID}>
                                      {p.productCode} - {p.productDescription}
                                    </MenuItem>
                                  ))}
                              </Select>
                            )}
                          </FormControl>
                        ) : (
                          `${item.productCode} - ${item.productDescription}`
                        )}
                      </TableCell>

                      <TableCell className={classes.bodyCell}>
                        {item.isNew || item.isEditing ? (
                          <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                            <TextField
                              size="small"
                              variant="outlined"
                              value={item.maxPostingQtyAllowed}
                              onChange={(e) => handleMaxQtyChange(index, e.target.value)}
                              placeholder="Type Text Here"
                              inputProps={{ inputMode: "numeric", style: { fontSize: 13 } }}
                            />
                            <Box display="flex" flexDirection="column" alignItems="center">
                              <IconButton
                                size="small"
                                onClick={() => item.isNew ? handleDelete(index) : handleEditCancel(index)}
                                title="Cancel"
                              >
                                <CloseIcon style={{ fontSize: 16, color: "#757575" }} />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleSaveRow(index)}
                                title="Save"
                                disabled={
                                  !item.maxPostingQtyAllowed ||
                                  parseFloat(item.maxPostingQtyAllowed) <= 0
                                }
                              >
                                <SaveIcon style={{ fontSize: 16 }} className={classes.saveRowIcon} />
                              </IconButton>
                            </Box>
                          </Box>
                        ) : (
                          <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                            {formatWithCommas(item.maxPostingQtyAllowed)}
                            {canEdit && (
                              <IconButton size="small" onClick={() => handleEditStart(index)} title="Edit">
                                <EditIcon style={{ color: "orange", fontSize: 20 }} />
                              </IconButton>
                            )}
                          </Box>
                        )}
                      </TableCell>

                      <TableCell className={classes.bodyCell}>{item.uom || ""}</TableCell>

                      <TableCell className={classes.bodyCell} style={{ borderRight: "none" }}>
                        {canEdit && (
                          <IconButton size="small" onClick={() => handleDelete(index)} title="Delete">
                            <DeleteIcon fontSize="small" className={classes.deleteIcon} />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {canEdit && (
            <Box mt={2}>
              <Button
                className={classes.addRowBtn}
                onClick={addNewRow}
                disabled={productLoading}
                startIcon={
                  productLoading
                    ? <CircularProgress size={14} />
                    : <span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
                }
              >
                Add New Row
              </Button>
            </Box>
          )}
        </Box>

        {canEdit && (
          <Box display="flex" flexDirection="column" style={{ gap: 10, minWidth: 150 }}>
            <Button
              className={classes.saveBtn}
              onClick={handleSaveSelection}
              disabled={!isUpdated || saveLoading}
              style={{ opacity: !isUpdated ? 0.7 : 1, color: "#fff" }}
            >
              {saveLoading ? (
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                  Saving...
                  <CircularProgress size={14} style={{ color: "#fff" }} />
                </Box>
              ) : (
                "Save Selection"
              )}
            </Button>
            <Button
              className={classes.resetBtn}
              onClick={resetSelection}
              disabled={!isUpdated}
              style={{ opacity: !isUpdated ? 0.7 : 1 }}
            >
              Reset Selection
            </Button>
          </Box>
        )}
      </Box>
    </div>
  );
};

export default ShipmentConfigTab;
